import { useRef, useState, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import httpClient from '../utils/HttpClient';

const EXPIRY_BUFFER_SECONDS = 60; // Fetch a new URL 60s before actual expiry

/**
 * Parses the `Expires` query parameter from an OSS signed URL
 * and returns the expiry time as a Unix timestamp (seconds).
 * Returns 0 if parsing fails (treat as expired).
 */
function getExpiryTimestamp(url) {
    try {
        const parsed = new URL(url);
        const expires = parseInt(parsed.searchParams.get('Expires'), 10);
        return isNaN(expires) ? 0 : expires;
    } catch {
        return 0;
    }
}

/**
 * Checks whether a signed URL is expired (or within the safety buffer).
 */
function isUrlExpired(url) {
    const expiresAt = getExpiryTimestamp(url);
    const nowSeconds = Math.floor(Date.now() / 1000);
    return nowSeconds >= expiresAt - EXPIRY_BUFFER_SECONDS;
}

/**
 * Custom hook to manage OSS signed URLs with expiry-aware caching.
 *
 * - Caches signed URLs per `magazineId` so repeated opens don't re-fetch.
 * - Checks the `Expires` param before returning a cached URL.
 * - If expired (or within buffer), fetches a fresh signed URL from the backend.
 *
 * Usage:
 *   const { getValidUrl, clearCache } = useSignedUrl();
 *   const { url, totalPage } = await getValidUrl(magazineId);
 */
export default function useSignedUrl() {
    const cacheRef = useRef({}); // { [magazineId]: { url, totalPage } }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Fetches a fresh signed URL from the backend and resolves total PDF pages.
     */
    const fetchFreshUrl = useCallback(async (magazineId) => {
        const response = await httpClient
            .get(`/magazines/view/${magazineId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw new Error(err?.message || 'Failed to fetch signed URL');
            });

        const fileUrl = response.fileUrl;
        if (!fileUrl) {
            throw new Error('No fileUrl returned from server');
        }

        // Resolve total page count from the PDF
        let totalPage = 1;
        try {
            const pdfDocument = await pdfjs.getDocument(fileUrl).promise;
            totalPage = pdfDocument.numPages;
        } catch (err) {
            console.error('useSignedUrl: Failed to extract PDF total pages', err);
        }

        return { url: fileUrl, totalPage };
    }, []);

    /**
     * Returns a valid (non-expired) signed URL for the given magazineId.
     * Uses cache if the URL is still valid; fetches fresh otherwise.
     */
    const getValidUrl = useCallback(
        async (magazineId) => {
            // Check cache first
            const cached = cacheRef.current[magazineId];
            if (cached && !isUrlExpired(cached.url)) {
                return cached;
            }

            // Cache miss or expired — fetch fresh
            setLoading(true);
            setError(null);
            try {
                const result = await fetchFreshUrl(magazineId);
                cacheRef.current[magazineId] = result;
                return result;
            } catch (err) {
                setError(err);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [fetchFreshUrl]
    );

    /**
     * Clears the cache for a specific magazineId, or all if none provided.
     */
    const clearCache = useCallback((magazineId) => {
        if (magazineId) {
            delete cacheRef.current[magazineId];
        } else {
            cacheRef.current = {};
        }
    }, []);

    return { getValidUrl, clearCache, loading, error };
}
